import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { storagePeriodPrefix } from '../../../lib/storage-path';
import { useVertical } from '../../../context/VerticalContext';
import { X, UploadCloud, File, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon, RotateCcw, Clock, AlertTriangle, Link2, Camera, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { errorMessage, reportError } from '../../../lib/errors';

interface ActiveJobRow {
    id: string;
    job_number: number | null;
    title: string | null;
    status: string | null;
    customer_name: string | null;
    vehicle_reg: string | null;
}

export type UploadDocType = 'receipt' | 'credit_note' | 'supplier_statement';

interface ReceiptUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void; // Fix 3: callback to refresh parent data
    // When set to 'credit_note' or 'supplier_statement', the OCR pipeline routes
    // the file to credit-matching logic instead of standard expense capture.
    docType?: UploadDocType;
    // Per-context copy. Defaults to receipt copy when omitted.
    title?: string;
    subtitle?: string;
    // Hide the optional job-link selector — not relevant for statement/credit-note uploads.
    showJobLink?: boolean;
    // Opens the Review queue (drains needs_review / manual_entry items). When
    // provided, the "Needs Review" chip routes here instead of a placeholder.
    onOpenReview?: () => void;
}

interface UploadStatus {
    id: string;
    file: File;
    status: 'pending' | 'uploading' | 'queued' | 'processing' | 'auto_verified' | 'needs_review' | 'manual_entry' | 'done' | 'completed' | 'error' | 'failed' | 'timed_out';
    progress: number;
    queueId?: string;
    error?: string;
    processingStartedAt?: number; // Fix 2: timeout tracking
    fileHash?: string; // Fix 4: duplicate detection
}

// Fix 2: Timeout constants
const TIMEOUT_WARNING_MS = 45_000; // 45 seconds
const TIMEOUT_FAIL_MS = 90_000; // 90 seconds

// Terminal states — when ALL items reach one of these, auto-close
const TERMINAL_STATES = new Set(['done', 'auto_verified', 'completed', 'needs_review', 'manual_entry', 'error', 'failed', 'timed_out']);

// --- Fix 4: File hashing utility ---
async function hashFile(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hash))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

export function ReceiptUploadModal({
    isOpen,
    onClose,
    onSuccess,
    docType,
    title,
    subtitle,
    showJobLink = true,
    onOpenReview,
}: ReceiptUploadModalProps) {
    const { activeOrgId } = useVertical();
    const [userId, setUserId] = useState<string | null>(null);
    const [uploads, setUploads] = useState<UploadStatus[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [duplicatePrompt, setDuplicatePrompt] = useState<{
        file: File;
        hash: string;
        existingId: string;
        existingDate: string;
    } | null>(null);
    // Optional job link — applies to all uploads in this modal session. Under
    // cash basis, this is what enables fn_get_working_capital to relax the
    // "parts still to buy" reservation against the matching job.
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    const [activeJobs, setActiveJobs] = useState<ActiveJobRow[]>([]);

    // Get current user ID
    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => {
            setUserId(data.session?.user?.id || null);
        });
    }, []);

    // Fetch active jobs whenever the modal opens. Active = anything that isn't
    // completed/cancelled/draft, so the user can link a supplier receipt to
    // the job it's for. Limited to a sensible 50 — orgs with that many open
    // jobs probably need a search box, but that's later.
    useEffect(() => {
        if (!isOpen || !activeOrgId) return;
        const fetchJobs = async () => {
            const { data, error } = await supabase
                .from('jobs')
                .select('id, job_number, title, status, customer:customers(full_name), vehicle:vehicles(vrm)')
                .eq('org_id', activeOrgId)
                .is('deleted_at', null)
                .not('status', 'in', '(completed,cancelled,draft)')
                .order('created_at', { ascending: false })
                .limit(50);
            if (error) {
                // Don't swallow — a schema drift here silently empties the dropdown.
                console.error('ReceiptUploadModal: failed to load active jobs', error);
                toast.error('Could not load jobs to link');
                setActiveJobs([]);
                return;
            }
            setActiveJobs((data ?? []).map((j: any) => ({
                id: j.id,
                job_number: j.job_number ?? null,
                title: j.title,
                status: j.status,
                customer_name: j.customer?.full_name?.trim() || null,
                vehicle_reg: j.vehicle?.vrm ?? null,
            })));
        };
        fetchJobs();
        // Reset selection when reopening — don't carry stale link across sessions.
        setSelectedJobId('');
    }, [isOpen, activeOrgId]);

    // --- Fix 5: Unique realtime channel ---
    useEffect(() => {
        if (!isOpen || !activeOrgId) return;

        const activeQueueIds = uploads.map(u => u.queueId).filter(Boolean) as string[];
        if (activeQueueIds.length === 0) return;

        const channelName = `ocr-upload-${activeOrgId}-${Date.now()}`;
        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'processing_queue',
                    filter: `org_id=eq.${activeOrgId}`,
                },
                (payload) => {
                    const updatedRow = payload.new;
                    setUploads(prev => prev.map(item => {
                        if (item.queueId === updatedRow.id) {
                            // Fix 1: Map 'done' and 'error' statuses correctly
                            const newStatus = updatedRow.status as UploadStatus['status'];
                            return {
                                ...item,
                                status: newStatus,
                                error: updatedRow.last_error || (newStatus === 'error' ? 'Processing failed on server' : undefined),
                                // Clear the timeout timer on terminal state
                                ...(TERMINAL_STATES.has(newStatus) ? { processingStartedAt: undefined } : {})
                            };
                        }
                        return item;
                    }));
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeOrgId, uploads.map(u => u.queueId).join(',')]);

    // --- Fix 2: Timeout checker ---
    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            const now = Date.now();
            setUploads(prev => prev.map(item => {
                if (!item.processingStartedAt) return item;
                if (TERMINAL_STATES.has(item.status)) return item;

                const elapsed = now - item.processingStartedAt;

                if (elapsed >= TIMEOUT_FAIL_MS) {
                    // Hard timeout: mark as timed_out
                    return {
                        ...item,
                        status: 'timed_out' as const,
                        error: 'OCR timed out. The file may still be processing — you can retry or check back later.',
                        processingStartedAt: undefined
                    };
                }
                // The warning state is handled in the UI via the elapsed time check
                return item;
            }));
        }, 5000); // Check every 5 seconds

        return () => clearInterval(interval);
    }, [isOpen]);

    // --- Fix 1: Auto-close when all items reach terminal state ---
    useEffect(() => {
        if (!isOpen || uploads.length === 0) return;

        const allTerminal = uploads.every(u => TERMINAL_STATES.has(u.status));
        if (!allTerminal) return;

        // Count successes vs failures
        const successCount = uploads.filter(u => ['done', 'auto_verified', 'completed'].includes(u.status)).length;
        const reviewCount = uploads.filter(u => ['needs_review', 'manual_entry'].includes(u.status)).length;
        const failCount = uploads.filter(u => ['error', 'failed', 'timed_out'].includes(u.status)).length;

        // Build summary toast
        const parts: string[] = [];
        if (successCount > 0) parts.push(`${successCount} processed`);
        if (reviewCount > 0) parts.push(`${reviewCount} need review`);
        if (failCount > 0) parts.push(`${failCount} failed`);

        const timer = setTimeout(() => {
            // When something needs review, the summary must be actionable — the
            // modal is about to auto-close, so a plain toast would strand the
            // user with "N need review" and nowhere to go. Offer a "Review now"
            // button that opens the Review Queue.
            if (reviewCount > 0 && onOpenReview) {
                toast(
                    (t) => (
                        <span className="flex items-center gap-3">
                            <span>📄 {parts.join(', ')}</span>
                            <button
                                onClick={() => {
                                    toast.dismiss(t.id);
                                    onOpenReview();
                                }}
                                className="text-xs font-semibold bg-gold-fill/20 text-gold px-2.5 py-1 rounded-full hover:bg-gold-fill/30 transition-colors whitespace-nowrap"
                            >
                                Review now
                            </button>
                        </span>
                    ),
                    { icon: failCount > 0 ? '⚠️' : undefined, duration: 6000 }
                );
            } else if (failCount === 0) {
                toast.success(`📄 ${parts.join(', ')}`, { duration: 4000 });
            } else {
                toast(parts.join(', '), { icon: '⚠️', duration: 5000 });
            }

            // Reset and close
            setUploads([]);
            onClose();
            // Fix 3: Trigger parent refresh
            if (successCount > 0 || reviewCount > 0) {
                onSuccess?.();
            }
        }, 1500); // 1.5 second delay so user can see final state

        return () => clearTimeout(timer);
    }, [isOpen, uploads, onClose, onSuccess, onOpenReview]);

    // --- Fix 4: Duplicate detection ---
    const checkForDuplicate = useCallback(async (file: File, hash: string): Promise<{ isDuplicate: boolean; existingId?: string; existingDate?: string }> => {
        if (!activeOrgId) return { isDuplicate: false };

        const { data, error: fetchError } = await supabase
            .from('processing_queue')
            .select('id, created_at')
            .eq('org_id', activeOrgId)
            .eq('metadata->>file_hash', hash)
            .in('status', ['done', 'auto_verified', 'needs_review', 'completed'])
            .limit(1)
            .single();
        if (fetchError) reportError(fetchError, 'receipt-upload-modal.processing-queue');

        if (data) {
            const date = new Date(data.created_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric'
            });
            return { isDuplicate: true, existingId: data.id, existingDate: date };
        }
        return { isDuplicate: false };
    }, [activeOrgId]);

    const handleFilesSelected = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        const newUploads: UploadStatus[] = [];

        for (const file of fileArray) {
            const hash = await hashFile(file);

            // Check for duplicates
            const { isDuplicate, existingId, existingDate } = await checkForDuplicate(file, hash);

            if (isDuplicate && existingId && existingDate) {
                // Show duplicate prompt for this file
                setDuplicatePrompt({ file, hash, existingId, existingDate });
                // Wait for user decision (handled separately)
                return; // Process one at a time for duplicates
            }

            newUploads.push({
                id: Math.random().toString(36).substring(7),
                file,
                status: 'pending',
                progress: 0,
                fileHash: hash
            });
        }

        if (newUploads.length > 0) {
            setUploads(prev => [...prev, ...newUploads]);
            await Promise.all(newUploads.map(u => processUpload(u, false)));

            try {
                await supabase.functions.invoke('ocr-queue-trigger', { body: {} });
            } catch (err) {
                    console.error('OCR queue trigger failed; the receipt is queued and will be picked up by the next sweep:', err);
                }
        }
    };

    // --- Fix 4: Handle duplicate decision ---
    const handleDuplicateReplace = async () => {
        if (!duplicatePrompt || !activeOrgId) return;

        const { file, hash, existingId } = duplicatePrompt;
        setDuplicatePrompt(null);

        try {
            // 1. Find and delete the old ledger entry linked to this queue item
            const { data: oldQueue, error: oldQueueError } = await supabase
                .from('processing_queue')
                .select('result, metadata')
                .eq('id', existingId)
                .single();
            if (oldQueueError) reportError(oldQueueError, 'receipt-upload-modal.processing-queue');

            if (oldQueue?.result?.ledger_header_id) {
                // Audited soft-delete via RPC (verifies org ownership, soft-deletes
                // header + line items, reverses org_financial_cache, unlinks
                // dependents, writes audit_logs — all in one transaction).
                // Never hard-delete a ledger row from the browser.
                const { error: voidError } = await supabase.rpc('fn_void_ledger_header', {
                    p_header_id: oldQueue.result.ledger_header_id,
                });
                if (voidError) throw voidError;
            }

            // 2. Reset the old queue item to pending. The job picked NOW
            // replaces the one picked for the first upload:
            // fn_process_standard_expense links to metadata.job_id ahead of
            // any automatic match, so a stale one would post to the old job.
            const { error: resetError } = await supabase
                .from('processing_queue')
                .update({
                    status: 'pending', result: null, processed_at: null, doc_type: null,
                    metadata: { ...(oldQueue?.metadata ?? {}), job_id: selectedJobId || null },
                })
                .eq('id', existingId);
            if (resetError) throw resetError;

            // 3. Re-trigger processing
            const upload: UploadStatus = {
                id: Math.random().toString(36).substring(7),
                file,
                status: 'queued',
                progress: 100,
                queueId: existingId,
                fileHash: hash
            };

            setUploads(prev => [...prev, upload]);

            const { error: triggerError } = await supabase.functions.invoke('ocr-queue-trigger', { body: {} });
            if (triggerError) throw triggerError;

            // Start timeout tracking
            setUploads(prev => prev.map(u =>
                u.queueId === existingId ? { ...u, status: 'processing', processingStartedAt: Date.now() } : u
            ));

        } catch {
            toast.error('Failed to reprocess. Try again.');
        }
    };

    const handleDuplicateSkip = () => {
        setDuplicatePrompt(null);
    };

    const processUpload = async (upload: UploadStatus, triggerWebhook: boolean = true) => {
        if (!userId || !activeOrgId) return;

        let queueRecordId: string | undefined = undefined;

        try {
            // 1. Mark as uploading
            setUploads(prev => prev.map(u => u.id === upload.id ? { ...u, status: 'uploading', progress: 10 } : u));

            // Shared bucket layout: {org}/{YYYY}/Q{quarter}/{MM}/{uuid}.{ext}
            const fileExt = (upload.file.name.split('.').pop() || 'bin').toLowerCase();
            const filePath = `${storagePeriodPrefix(activeOrgId)}/${crypto.randomUUID()}.${fileExt}`;

            // 2. Upload to Storage
            const { error: uploadError } = await supabase.storage
                .from('expense-receipts')
                .upload(filePath, upload.file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) throw uploadError;

            setUploads(prev => prev.map(u => u.id === upload.id ? { ...u, progress: 50 } : u));

            // 3. Insert into Processing Queue (with file hash for duplicate detection)
            const { data: queueData, error: queueError } = await supabase
                .from('processing_queue')
                .insert({
                    org_id: activeOrgId,
                    source: 'upload',
                    file_path: filePath,
                    file_name: upload.file.name,
                    file_type: upload.file.type || 'application/octet-stream',
                    file_size: upload.file.size,
                    status: 'pending',
                    // Seed doc_type so the OCR processor routes correctly when the caller knows
                    // it's a credit document. For 'receipt' we leave doc_type null and
                    // let OCR classify (existing behaviour).
                    doc_type: docType && docType !== 'receipt' ? docType : null,
                    metadata: {
                        uploaded_by: userId,
                        file_hash: upload.fileHash,
                        // User-selected job link. fn_process_standard_expense
                        // reads it from the queue row and links the posted
                        // expense to this job ahead of any automatic match.
                        job_id: selectedJobId || null
                    }
                })
                .select('id')
                .single();

            if (queueError) throw queueError;
            queueRecordId = queueData.id;

            // 4. Mark as queued + start timeout tracking
            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'queued',
                progress: 100,
                queueId: queueRecordId,
                processingStartedAt: Date.now() // Fix 2: start timer
            } : u));

            // 5. Nudge the OCR processor (unless skipped for batching)
            if (triggerWebhook) {
                const { data: triggerData, error: triggerError } = await supabase.functions.invoke('ocr-queue-trigger', { body: {} });

                if (triggerError || triggerData?.error) {
                    let uiError = 'OCR service is currently unavailable. Please try again later.';
                    if (triggerData?.error) uiError = triggerData.error;
                    throw new Error(uiError);
                }
            }

        } catch (err) {

            if (queueRecordId) {
                const { error: updateError } = await supabase.from('processing_queue').update({
                    status: 'failed',
                    metadata: {
                        uploaded_by: userId,
                        file_hash: upload.fileHash,
                        error: errorMessage(err) || 'Upload failed due to service unavailability'
                    }
                }).eq('id', queueRecordId);

                if (updateError) {
                    // Failing to RECORD the failure leaves the queue row stuck in
                    // `processing`, so the UI shows the receipt as still in flight
                    // forever. The user is already being told the upload failed;
                    // this is for whoever has to explain the stuck row later.
                    console.error('Could not mark queued upload as failed; it may stay stuck in processing:', updateError);
                }
            }

            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'failed',
                error: errorMessage(err) || 'Upload failed',
                processingStartedAt: undefined
            } : u));
        }
    };

    const retryUpload = async (upload: UploadStatus) => {
        if (!upload.queueId) {
            processUpload(upload);
            return;
        }

        try {
            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'queued',
                progress: 100,
                error: undefined,
                processingStartedAt: Date.now() // Reset timer
            } : u));

            const { error: resetError } = await supabase
                .from('processing_queue')
                .update({ status: 'pending' })
                .eq('id', upload.queueId);

            if (resetError) throw resetError;

            const { data: triggerData, error: triggerError } = await supabase.functions.invoke('ocr-queue-trigger', { body: {} });

            if (triggerError || triggerData?.error) {
                let uiError = 'OCR service is currently unavailable. Please try again later.';
                if (triggerData?.error) uiError = triggerData.error;
                throw new Error(uiError);
            }

        } catch (err) {

            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'failed',
                error: errorMessage(err) || 'Retry failed',
                processingStartedAt: undefined
            } : u));
        }
    };

    // --- BATCH PROCESSING ---
    const handleProcessBatch = async () => {
        const queuedUploads = uploads.filter(u => u.status === 'queued' && u.queueId);

        if (queuedUploads.length === 0) return;

        // Mark them all as processing with timeout tracking
        setUploads(prev => prev.map(u =>
            u.status === 'queued' ? { ...u, status: 'processing', processingStartedAt: Date.now() } : u
        ));

        try {
            const { data: triggerData, error: triggerError } = await supabase.functions.invoke('ocr-queue-trigger', { body: {} });

            if (triggerError || triggerData?.error) {
                let uiError = 'OCR service is currently unavailable. Please try again later.';
                if (triggerData?.error) uiError = triggerData.error;
                throw new Error(uiError);
            }

        } catch (err) {

            setUploads(prev => prev.map(u =>
                u.status === 'processing' ? {
                    ...u,
                    status: 'failed',
                    error: errorMessage(err) || 'Trigger failed due to service unavailability',
                    processingStartedAt: undefined
                } : u
            ));

            const queueIdsToFail = queuedUploads.map(u => u.queueId) as string[];
            if (queueIdsToFail.length > 0) {
                // This marks the queue rows as failed so the user can retry.
                // Its own error was discarded, which meant a failure to record the
                // failure left the rows stuck in `processing` forever, with the UI
                // showing them as still in flight. Nothing to show the user here —
                // they are already being told the upload failed — but it must not
                // vanish.
                const { error: batchUpdateError } = await supabase.from('processing_queue').update({
                    status: 'failed',
                    metadata: {
                        uploaded_by: userId,
                        error: errorMessage(err, 'Trigger failed due to service unavailability'),
                        batch_failure: true
                    }
                }).in('id', queueIdsToFail);

                if (batchUpdateError) {
                    console.error('Could not mark queued uploads as failed; they may stay stuck in processing:', batchUpdateError);
                }
            }
        }
    };

    if (!isOpen) return null;

    const hasQueuedItems = uploads.some(u => u.status === 'queued');
    const isProcessingAny = uploads.some(u => u.status === 'processing' || u.status === 'uploading' || u.status === 'pending');

    return (
        <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-panel border border-line rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-line bg-raised">
                    <div>
                        <h2 className="text-xl font-bold text-fg">{title || 'Upload Receipts & Invoices'}</h2>
                        <p className="text-sm text-fg-3 mt-1">{subtitle || 'Files are securely stored and processed by our AI OCR.'}</p>
                    </div>
                    <button
                        onClick={() => {
                            setUploads([]);
                            onClose();
                        }}
                        className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">

                    {/* --- Fix 4: Duplicate Confirmation Dialog --- */}
                    {duplicatePrompt && (
                        <div className="mb-6 bg-gold-fill/10 border border-gold/30 rounded-xl p-5 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-gold">Duplicate File Detected</h4>
                                    <p className="text-xs text-fg-3 mt-1">
                                        <span className="text-fg font-medium">{duplicatePrompt.file.name}</span> was already uploaded on{' '}
                                        <span className="text-gold">{duplicatePrompt.existingDate}</span>.
                                    </p>
                                    <div className="flex gap-2 mt-4">
                                        <button
                                            onClick={handleDuplicateSkip}
                                            className="px-4 py-2 bg-raised hover:bg-pressed text-fg-2 rounded-lg text-sm font-medium transition-colors border border-line-strong"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleDuplicateReplace}
                                            className="px-4 py-2 bg-gold-fill hover:bg-gold-fill text-on-gold rounded-lg text-sm font-semibold transition-colors shadow-lg"
                                        >
                                            Replace & Reprocess
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Optional job link — applies to every file in this session.
                        Disabled once uploads have started so the link doesn't
                        change mid-batch. Hidden for credit-note/statement
                        uploads where the link is inferred by matching. */}
                    {showJobLink && (
                    <div className="mb-5">
                        <label htmlFor="receipt-job-link" className="flex items-center gap-1.5 text-xs font-medium text-fg-3 uppercase tracking-wider mb-2">
                            <Link2 className="w-3.5 h-3.5" />
                            Link to job (optional)
                        </label>
                        <select
                            id="receipt-job-link"
                            value={selectedJobId}
                            onChange={(e) => setSelectedJobId(e.target.value)}
                            disabled={uploads.length > 0}
                            className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent disabled:opacity-60 disabled:cursor-not-allowed text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                        >
                            <option value="">— No job link —</option>
                            {activeJobs.map((job) => {
                                const jobNo = job.job_number != null ? `JOB-${String(job.job_number).padStart(4, '0')} · ` : '';
                                const tag = job.vehicle_reg ? ` · ${job.vehicle_reg}` : '';
                                const customer = job.customer_name || 'Unnamed customer';
                                const titlePart = job.title ? ` · ${job.title}` : '';
                                return (
                                    <option key={job.id} value={job.id}>
                                        {jobNo}{customer}{tag}{titlePart}
                                    </option>
                                );
                            })}
                        </select>
                        {selectedJobId && (
                            <p className="text-[11px] text-fg-4 mt-1.5">
                                This supplier purchase will reduce the Working Capital reservation on this job.
                            </p>
                        )}
                    </div>
                    )}

                    {/* Dropzone */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors
              ${isDragging ? 'border-accent bg-accent/10' : 'border-line-strong hover:border-line-glow bg-card/50'}
            `}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            handleFilesSelected(e.dataTransfer.files);
                        }}
                    >
                        <UploadCloud className="w-10 h-10 text-fg-3 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-fg mb-2">Add your receipts</h3>
                        <p className="text-sm text-fg-3 mb-5 max-w-md mx-auto">
                            Snap them at the counter, or pick files you already have.
                            Select as many as you like — PDF, PNG or JPG, up to 10MB each.
                        </p>

                        {/* Two explicit routes in. The camera input carries
                            `capture`, so on a phone it opens the camera directly
                            instead of dropping the user into a file browser to
                            go hunting for the photo they have not taken yet. */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                            <button
                                onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                                className="flex-1 flex items-center justify-center gap-2 fd-btn-primary px-5 py-3 rounded-lg text-sm font-bold transition-all active:scale-[0.98]"
                            >
                                <Camera className="w-4 h-4" />
                                Take photo
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                className="flex-1 flex items-center justify-center gap-2 bg-raised hover:bg-pressed text-fg px-5 py-3 rounded-lg text-sm font-medium transition-colors"
                            >
                                <FolderOpen className="w-4 h-4" />
                                Browse files
                            </button>
                        </div>

                        <p className="hidden sm:block text-xs text-fg-4 mt-4">
                            …or drag them anywhere onto this box.
                        </p>

                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            multiple
                            accept="image/jpeg,image/png,image/jpg,application/pdf"
                            onChange={(e) => handleFilesSelected(e.target.files)}
                        />
                        {/* Separate input: `capture` and a multi-select picker are
                            mutually exclusive in practice, so one button per intent. */}
                        <input
                            type="file"
                            ref={cameraInputRef}
                            className="hidden"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handleFilesSelected(e.target.files)}
                        />
                    </div>

                    {/* Upload List */}
                    {uploads.length > 0 && (
                        <div className="mt-8 space-y-3">
                            <h4 className="text-sm font-medium text-fg-3 uppercase tracking-wider mb-4">Processing Queue</h4>
                            {uploads.map((upload) => (
                                <div key={upload.id} className="bg-card border border-line rounded-lg p-4 flex items-center justify-between gap-4">

                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-raised flex items-center justify-center shrink-0">
                                            {upload.file.type.includes('pdf') ? <File className="w-5 h-5 text-info" /> : <ImageIcon className="w-5 h-5 text-info" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-fg truncate">{upload.file.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-fg-4">{(upload.file.size / 1024 / 1024).toFixed(2)} MB</span>
                                                <span className="text-fg-4">&bull;</span>
                                                <span className="text-xs font-medium" style={{ color: getStatusColor(upload.status) }}>
                                                    {getStatusText(upload.status, upload.processingStartedAt)}
                                                </span>
                                            </div>
                                            {upload.error && (
                                                <p className="text-xs text-danger mt-1 line-clamp-2">{upload.error}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Status Icons */}
                                    <div className="shrink-0 flex items-center">
                                        {(upload.status === 'uploading' || upload.status === 'pending') && (
                                            <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                                        )}
                                        {(upload.status === 'queued' || upload.status === 'processing') && (
                                            (() => {
                                                const isWarning = upload.processingStartedAt && (Date.now() - upload.processingStartedAt) > TIMEOUT_WARNING_MS;
                                                return isWarning
                                                    ? <Clock className="w-5 h-5 text-gold animate-pulse" />
                                                    : <Loader2 className="w-5 h-5 text-accent animate-spin" />;
                                            })()
                                        )}
                                        {/* Fix 1: Map 'done' as success */}
                                        {(['auto_verified', 'completed', 'done'] as string[]).includes(upload.status) && (
                                            <CheckCircle2 className="w-5 h-5 text-success" />
                                        )}
                                        {(upload.status === 'needs_review' || upload.status === 'manual_entry') && (
                                            <button
                                                onClick={() => {
                                                    if (onOpenReview) {
                                                        // Hand off to the Review queue. Reset our own
                                                        // upload list and close so the queue owns focus.
                                                        setUploads([]);
                                                        onClose();
                                                        onOpenReview();
                                                    } else {
                                                        toast('Open Expenses → Review Queue to action this.', { icon: 'ℹ️' });
                                                    }
                                                }}
                                                className="text-xs bg-gold-fill/20 text-gold px-3 py-1.5 rounded-full font-medium hover:bg-gold-fill/30 transition-colors"
                                            >
                                                Needs Review
                                            </button>
                                        )}
                                        {(['failed', 'error', 'timed_out'] as string[]).includes(upload.status) && (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => retryUpload(upload)}
                                                    className="p-1.5 bg-raised hover:bg-pressed rounded-md transition-colors text-fg-3 hover:text-fg group"
                                                    title="Retry Upload"
                                                >
                                                    <RotateCcw className="w-4 h-4 group-hover:-rotate-45 transition-transform" />
                                                </button>
                                                <AlertCircle className="w-5 h-5 text-danger" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer with Batch Button */}
                {uploads.length > 0 && (
                    <div className="p-6 border-t border-line bg-raised flex justify-end">
                        <button
                            onClick={handleProcessBatch}
                            disabled={!hasQueuedItems || isProcessingAny}
                            className="fd-btn-primary disabled:opacity-50 px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                        >
                            {isProcessingAny ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Processing...
                                </>
                            ) : hasQueuedItems ? (
                                <>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Process Batch ({uploads.filter(u => u.status === 'queued').length})
                                </>
                            ) : (
                                <>All Done</>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// Helper functions for UI mapping
function getStatusColor(status: string) {
    switch (status) {
        case 'auto_verified':
        case 'completed':
        case 'done': return 'var(--fd-success)';
        case 'needs_review':
        case 'manual_entry': return 'var(--fd-gold)';
        case 'failed':
        case 'error': return 'var(--fd-danger)';
        case 'timed_out': return 'var(--fd-warning)';
        case 'queued':
        case 'processing': return 'var(--fds-accent)';
        default: return 'var(--fd-text-muted)';
    }
}

function getStatusText(status: string, processingStartedAt?: number) {
    // Fix 2: Show warning text if taking too long
    if ((status === 'queued' || status === 'processing') && processingStartedAt) {
        const elapsed = Date.now() - processingStartedAt;
        if (elapsed > TIMEOUT_WARNING_MS) {
            return 'Taking longer than expected...';
        }
    }

    switch (status) {
        case 'pending': return 'Preparing...';
        case 'uploading': return 'Uploading file...';
        case 'queued': return 'In processing queue...';
        case 'processing': return 'AI is parsing...';
        case 'auto_verified':
        case 'completed':
        case 'done': return 'Successfully filed!';
        case 'needs_review': return 'Requires review';
        case 'manual_entry': return 'Could not read';
        case 'failed':
        case 'error': return 'Processing failed';
        case 'timed_out': return 'Timed out';
        default: return status;
    }
}
